"use client";

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import {
  Bar,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import { useFinance, useMonthlyAgg, useTagPolicy, tagPolicyIsDefault, isIncome, isSpending, monthlyWindow } from '@/lib/finance/finance-data-context'
import { currentMonthKey, dateToMs, fmtMonthShort, fmtUsd } from '@/lib/finance/format'
import { COLORED_GROUPS, groupColor, groupOf, OTHER_COLOR } from '@/lib/finance/categories'
import { BUCKET_ORDER, bucketOf, bucketRange, fmtBucket, fmtBucketShort, shadeOf, type Bucket } from '@/lib/finance/buckets'
import { Card, LegendKey, ResetFiltersChip, ScopeMenu, Segmented, PageHeader, Pill } from '@/components/finance/ui'
import { TagMenu } from '@/components/finance/TagMenu'
import { CategoryMenu, type CatNode } from '@/components/finance/CategoryMenu'
import { usePageRange } from '@/components/finance/RangePicker'
import { rangeStateFor, resolveRange, PAGE_RANGE_PRESETS } from '@/lib/finance/range'
import { navigateInShell } from '@/lib/clientNav'
import { useSessionState } from '@/lib/finance/usePersistedState'
import { ChartTooltip, barCursor } from '@/components/finance/ChartTooltip'
import { xAxisProps, yAxisProps, gridProps, BAR_MAX, BAR_RADIUS, CHART_MARGIN } from '@/components/finance/chartDefaults'
import { SpendingDonut } from '@/components/finance/SpendingDonut'

type Measure = 'spending' | 'income'
type View = 'time' | 'category' | 'merchant'

// How many rows the by-merchant list shows (last = All, an unbounded slice).
const MERCHANT_LIMITS: { value: string; label: string; n: number }[] = [
  { value: '25', label: '25', n: 25 },
  { value: '50', label: '50', n: 50 },
  { value: '100', label: '100', n: 100 },
  { value: '250', label: '250', n: 250 },
  { value: '500', label: '500', n: 500 },
  { value: 'all', label: 'All', n: Infinity },
]

// Density override for the over-time chart. 'auto' keeps the width-based pick;
// any explicit bucket forces that granularity regardless of screen width.
type BucketPref = Bucket | 'auto'
const BUCKET_OPTIONS: { value: BucketPref; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
]

// Ledger drill-downs are in-shell pushState navigations (clientNav): the
// finance app is one catch-all route, so this swaps the rendered page with
// zero network and the exact URL intact. (The old full-page
// window.location.assign existed to dodge the router cache restoring STALE
// search params over a drill's category — with pushState there is no router
// cache in the loop, and the freshly mounted Transactions page reads the
// live URL.) The Trends URL fully encodes drill state so Back restores it.
const goLedger = (url: string) => navigateInShell(url)

export function Trends() {
  const searchParams = useSearchParams()
  const { txns } = useFinance()
  const { policy: tagPolicy, reset: resetTagPolicy } = useTagPolicy()
  // Navigation state (measure / view / drills) lives in the URL — ?m= ?v= ?g=
  // ?b= — so browser Back/Forward walks between Trends states, and Back from
  // the Transactions page restores exactly the screen that linked out. (The
  // old localStorage persistence restored stale drill keys into mismatched
  // ranges — the "undefined undefined, NaN" drill card.) pushQuery adds a
  // real history entry via history.pushState; Next syncs useSearchParams from
  // it with no server round-trip (same pattern as the Transactions filters).
  const pushQuery = (mutate: (q: URLSearchParams) => void) => {
    const q = new URLSearchParams(window.location.search)
    mutate(q)
    const qs = q.toString()
    window.history.pushState(null, '', qs ? `/finance/trends?${qs}` : '/finance/trends')
  }
  // Same, but replaceState — for FILTER edits (category scope) rather than
  // navigation. A filter isn't a nav step, and CategoryMenu fires per checkbox
  // toggle, so replaceState keeps the URL shareable/reload-safe without spamming
  // Back with one entry per toggle. (Mirrors the Transactions page filters.)
  const replaceQuery = (mutate: (q: URLSearchParams) => void) => {
    const q = new URLSearchParams(window.location.search)
    mutate(q)
    const qs = q.toString()
    window.history.replaceState(null, '', qs ? `/finance/trends?${qs}` : '/finance/trends')
  }
  const measure: Measure = searchParams.get('m') === 'income' ? 'income' : 'spending'
  const viewParam = searchParams.get('v')
  const view: View = viewParam === 'category' || viewParam === 'merchant' ? viewParam : 'time'
  const drillGroup = searchParams.get('g')
  const drillBucketParam = searchParams.get('b')
  const setMeasure = (m: Measure) =>
    pushQuery((q) => {
      if (m === 'income') q.set('m', 'income')
      else q.delete('m')
      q.delete('g')
      q.delete('b')
    })
  const setView = (v: View) =>
    pushQuery((q) => {
      if (v === 'time') q.delete('v')
      else q.set('v', v)
      q.delete('g')
      q.delete('b')
    })
  // Drilling stamps the current range (?f=&t=) into the URL too — a drilled
  // bucket/group only means anything under the range it was clicked in, and
  // Back from the Transactions page remounts this page on the default range
  // otherwise. Cleared when the last drill closes.
  const stampRange = (q: URLSearchParams) => {
    q.set('f', startMonth)
    q.set('t', endMonth)
  }
  const clearRangeIfUndrilled = (q: URLSearchParams) => {
    if (!q.get('b') && !q.get('g')) {
      q.delete('f')
      q.delete('t')
    }
  }
  const setDrillGroup = (g: string | null) =>
    pushQuery((q) => {
      if (g) {
        q.set('g', g)
        stampRange(q)
      } else {
        q.delete('g')
        clearRangeIfUndrilled(q)
      }
    })
  const setDrillBucket = (b: string | null) =>
    pushQuery((q) => {
      // In the over-time view the group drill (?g=) lives INSIDE a bucket, so
      // changing or closing the bucket drops it. Without this, clicking a
      // different bar would silently re-drill into the previous bar's group
      // instead of opening the new bar's overview.
      if (q.get('b') !== b) q.delete('g')
      if (b) {
        q.set('b', b)
        stampRange(q)
      } else {
        q.delete('b')
        clearRangeIfUndrilled(q)
      }
    })
  // Density override for the over-time chart. Session-scoped: it survives a
  // drilldown round-trip but every fresh app open starts back at Auto — a
  // one-time tap on "Month" months ago shouldn't pin the chart forever
  // (localStorage persistence did exactly that). 'auto' defers to the
  // width-based pick below.
  const [bucketPref, setBucketPref] = useSessionState<BucketPref>('fin.trends.bucket', 'auto')
  // Drop the old forever-sticky key so previously-pinned devices reset to Auto.
  useEffect(() => {
    try { localStorage.removeItem('fin.trends.bucket') } catch { /* ignore */ }
  }, [])
  // How many merchants the by-merchant list shows. Defaults to the top 25;
  // bump it (or 'All') to see the long tail. Session-scoped so it survives a
  // drilldown round-trip but resets to 25 on a fresh open.
  const [merchantLimit, setMerchantLimit] = useSessionState<string>('fin.trends.merchantLimit', '25')
  // Over-time average line — off by default, session-sticky like the density pick.
  const [showAvg, setShowAvg] = useSessionState<'0' | '1'>('fin.trends.avg', '0')
  const merchantLimitN = MERCHANT_LIMITS.find((o) => o.value === merchantLimit)?.n ?? 25
  // Multi-select scope for the over-time chart: whole groups stack as group
  // bands, individual sub-categories as their own bands. Empty = all groups.
  // Category scope lives in the URL as the EXCLUDED categories per measure —
  // ?xs= (spend) / ?xi= (income), comma-joined category names, absent = all
  // selected — so the filter is shareable, bookmarkable, and survives reload,
  // and the same scope carries across every view. Written via replaceState so a
  // toggle isn't a history step (see replaceQuery).
  const xsStr = searchParams.get('xs')
  const xiStr = searchParams.get('xi')
  const exclSpend = useMemo(() => (xsStr ? xsStr.split(',').filter(Boolean) : []), [xsStr])
  const exclIncome = useMemo(() => (xiStr ? xiStr.split(',').filter(Boolean) : []), [xiStr])
  const setExclSpend = (next: string[]) =>
    replaceQuery((q) => (next.length ? q.set('xs', next.join(',')) : q.delete('xs')))
  const setExclIncome = (next: string[]) =>
    replaceQuery((q) => (next.length ? q.set('xi', next.join(',')) : q.delete('xi')))
  // Measure the chart's rendered width so bucket granularity adapts to screen
  // density (mobile portrait vs landscape vs desktop) — see `bucket` below.
  const chartRef = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState(0)
  useEffect(() => {
    const el = chartRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => setChartWidth(entries[0].contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Views build on the precomputed monthly statements (rollups.json); raw
  // transactions are only scanned for the by-merchant and income-by-category
  // views, which need per-transaction detail the statements don't carry.
  // Ranges anchor to TODAY: months with no imported transactions are shown
  // empty rather than snapping the window back to older data.
  const monthly = useMonthlyAgg()
  const nowMonth = currentMonthKey()
  const dataStart = monthly.length ? monthly[0].month : nowMonth
  const { startMonth, endMonth, setState: setRange, control } = usePageRange(dataStart, nowMonth)

  // Apply the URL's drill range (?f=&t=) whenever it disagrees with the live
  // range — this is what makes Back/Forward restore a drilled view after the
  // page remounts. The sync effect below writes the URL from the live range,
  // and the two settle: once applied, f/t === start/end and both no-op.
  const fParam = searchParams.get('f')
  const tParam = searchParams.get('t')
  // While an apply is in flight (URL → range), the sync effect must hold off:
  // the shared range picker state persists across pages, so on Back the live
  // range is whatever the Transactions page last set — without this flag the
  // sync effect would "correct" the URL from that stale live value (dropping
  // the drill) before the apply landed. The URL wins on (re)mount.
  const applyingRange = useRef(false)
  // The window the in-flight apply resolves to. A drill link that lands on a
  // standard range snaps to the preset chip (rangeStateFor), whose resolved
  // window can be WIDER than the link's f/t (an MTD link ends today; the chip
  // resolves through month-end) — so "the apply landed" must be judged
  // against this, not against f/t equality.
  const pendingRange = useRef<[string, string] | null>(null)
  useEffect(() => {
    if (!fParam || !tParam) return
    const mapped = rangeStateFor(fParam, tParam, dataStart, nowMonth)
    const [ms, me] = resolveRange(mapped, PAGE_RANGE_PRESETS, '1y', dataStart, nowMonth)
    if (ms === startMonth && me === endMonth) {
      // Window already live; just normalize the URL bounds to it (keeping the
      // drill's bucket key) so the two agree.
      if (fParam !== startMonth || tParam !== endMonth) {
        const q = new URLSearchParams(window.location.search)
        q.set('f', startMonth)
        q.set('t', endMonth)
        window.history.replaceState(null, '', `/finance/trends?${q.toString()}`)
      }
      return
    }
    applyingRange.current = true
    pendingRange.current = [ms, me]
    setRange(mapped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fParam, tParam])
  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    if (!q.get('f') && !q.get('t')) return
    if (q.get('f') === startMonth && q.get('t') === endMonth) {
      applyingRange.current = false
      return
    }
    if (applyingRange.current) {
      const [pf, pt] = pendingRange.current ?? []
      if (startMonth === pf && endMonth === pt) {
        // The apply landed on a snapped preset — normalize the URL to the
        // resolved window, keeping the drill's bucket key. Not a user move:
        // don't drop 'b'.
        applyingRange.current = false
        q.set('f', startMonth)
        q.set('t', endMonth)
        window.history.replaceState(null, '', `/finance/trends?${q.toString()}`)
      }
      return
    }
    // The user moved the range picker while drilled: keep the URL honest and
    // drop the bucket key (granularity likely changed; the guard would close
    // it anyway). Not a navigation step — replace.
    q.set('f', startMonth)
    q.set('t', endMonth)
    q.delete('b')
    const qs = q.toString()
    window.history.replaceState(null, '', qs ? `/finance/trends?${qs}` : '/finance/trends')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMonth, endMonth])

  // Legacy deep-link support (?view=category&group=<g>&from=<m>&to=<m> from
  // the Overview spending card): apply the range, then rewrite in place to
  // the canonical ?v=/?g= params — the URL IS the state now.
  const appliedParams = useRef(false)
  useEffect(() => {
    if (appliedParams.current) return
    appliedParams.current = true
    const group = searchParams.get('group')
    const v = searchParams.get('view')
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    if (!group && !v && !from && !to) return
    if (from && to) setRange(rangeStateFor(from, to, dataStart, nowMonth))
    const q = new URLSearchParams(window.location.search)
    for (const k of ['view', 'group', 'from', 'to']) q.delete(k)
    if (v === 'category' || group) {
      q.delete('m') // legacy links are always the spending measure
      q.set('v', 'category')
    }
    if (group) q.set('g', group)
    const qs = q.toString()
    window.history.replaceState(null, '', qs ? `/finance/trends?${qs}` : '/finance/trends')
  }, [searchParams, setRange])

  // startMonth/endMonth are full-date bounds (YYYY-MM-DD); the monthly window
  // iterates on their month prefixes, the transaction filter on the dates.
  const startM = startMonth.slice(0, 7)
  const endM = endMonth.slice(0, 7)
  const monthsInRange = useMemo(() => monthlyWindow(monthly, startM, endM), [monthly, startM, endM])
  const inRange = useMemo(
    () => (txns ?? []).filter((t) => t.date >= startMonth && t.date <= endMonth),
    [txns, startMonth, endMonth],
  )
  const txnsLoading = txns === null

  // Density-based bucketing: pick the FINEST bucket (day → week → month →
  // quarter → year) whose bar count fits the measured chart width, so a chart
  // is never denser than ~one bar per MIN_BAR_PX. This adapts automatically to
  // mobile portrait (narrow → coarser) vs landscape/desktop (wide → finer).
  const spanMonths = monthsInRange.length
  const spanDays = Math.round((dateToMs(endMonth) - dateToMs(startMonth)) / 86_400_000) + 1
  const MIN_BAR_PX = 10 // comfortable min per bar+gap; below this reads as too dense
  const maxBars = Math.max(6, Math.floor(((chartWidth || 900) - 60) / MIN_BAR_PX))
  const bucketCounts: Record<Bucket, number> = {
    day: spanDays,
    week: Math.ceil(spanDays / 7),
    month: spanMonths,
    quarter: Math.ceil(spanMonths / 3),
    year: Math.max(1, Math.ceil(spanMonths / 12)),
  }
  const autoBucket: Bucket = BUCKET_ORDER.find((b) => bucketCounts[b] <= maxBars) ?? 'year'
  // The user's density toggle wins when set; 'auto' falls back to the pick above.
  const bucket: Bucket = bucketPref === 'auto' ? autoBucket : bucketPref
  const usesDaily = bucket === 'day' || bucket === 'week'

  // Day/week buckets need per-day detail the monthly rollups don't carry —
  // aggregate the raw transactions per day (respecting the reimbursable toggle);
  // timeRows then groups those days into weeks when needed.
  const dailyRows = useMemo(() => {
    if (!usesDaily) return null
    const map = new Map<string, { month: string; income: number; spending: number; byGroup: Record<string, number>; byCategory: Record<string, number> }>()
    for (const t of inRange) {
      let r = map.get(t.date)
      if (!r) {
        r = { month: t.date, income: 0, spending: 0, byGroup: {}, byCategory: {} }
        map.set(t.date, r)
      }
      if (isSpending(t, tagPolicy)) {
        const s = -t.amount
        r.spending += s
        r.byGroup[groupOf(t.category)] = (r.byGroup[groupOf(t.category)] ?? 0) + s
        r.byCategory[t.category] = (r.byCategory[t.category] ?? 0) + s
      } else if (isIncome(t, tagPolicy)) {
        r.income += t.amount
      }
    }
    return [...map.values()].sort((a, b) => (a.month < b.month ? -1 : 1))
  }, [usesDaily, inRange, tagPolicy])

  // The rows the over-time chart + its drilldown aggregate from.
  const sourceRows = dailyRows ?? monthsInRange

  // ---- Category scope (checkbox trees, per measure) -----------------------
  // Stack order = group total, largest at the bottom. Colors follow the group
  // (fixed slots), so reordering never repaints a series.
  const stackGroups = useMemo(() => {
    const totals = new Map<string, number>()
    for (const m of sourceRows) {
      for (const [g, v] of Object.entries(m.byGroup)) {
        const slot = COLORED_GROUPS.includes(g as never) ? g : 'Other'
        if (v) totals.set(slot, (totals.get(slot) ?? 0) + v)
      }
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([g]) => g)
  }, [sourceRows])

  // Spending scope tree: stack-ordered groups → their tier-2 categories.
  const spendTree = useMemo(() => {
    const byGroup = new Map<string, Set<string>>()
    for (const m of sourceRows) {
      for (const cat of Object.keys(m.byCategory)) {
        const slot = COLORED_GROUPS.includes(groupOf(cat) as never) ? groupOf(cat) : 'Other'
        ;(byGroup.get(slot) ?? byGroup.set(slot, new Set()).get(slot)!).add(cat)
      }
    }
    return stackGroups
      .filter((g) => byGroup.has(g))
      .map((g): CatNode => {
        const cats = [...byGroup.get(g)!].sort((a, b) => a.localeCompare(b))
        return { key: 'g:' + g, label: g, cats, children: cats.map((c) => ({ key: 'c:' + c, label: c, cats: [c] })) }
      })
  }, [sourceRows, stackGroups])
  const spendAllCats = useMemo(() => spendTree.flatMap((n) => n.cats), [spendTree])
  const selectedSpend = useMemo(
    () => new Set(spendAllCats.filter((c) => !exclSpend.includes(c))),
    [spendAllCats, exclSpend],
  )
  const setSpendSel = (next: Set<string>) => setExclSpend(spendAllCats.filter((c) => !next.has(c)))

  // Income scope tree: a flat list of income categories present in range.
  const incomeCatsList = useMemo(() => {
    const totals = new Map<string, number>()
    for (const t of inRange) if (isIncome(t, tagPolicy)) totals.set(t.category, (totals.get(t.category) ?? 0) + t.amount)
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c)
  }, [inRange, tagPolicy])
  const incomeTree = useMemo(
    () => incomeCatsList.map((c): CatNode => ({ key: 'c:' + c, label: c, cats: [c] })),
    [incomeCatsList],
  )
  const selectedIncome = useMemo(
    () => new Set(incomeCatsList.filter((c) => !exclIncome.includes(c))),
    [incomeCatsList, exclIncome],
  )
  const setIncomeSel = (next: Set<string>) => setExclIncome(incomeCatsList.filter((c) => !next.has(c)))

  // The active category scope, applied to EVERY view (over-time, by-category,
  // by-merchant) so a filter set on one tab carries to the others. Keyed by the
  // current measure's selected set.
  const catInScope = (cat: string) => (measure === 'income' ? selectedIncome : selectedSpend).has(cat)

  // Legend/band hover actions (the "Only this" / "Exclude" popover): a spending
  // band is a GROUP ("Other" = every non-palette group), an income band is a
  // single category — expand each to its leaf categories, then narrow or drop.
  const bandCats = (name: string): string[] =>
    measure === 'income'
      ? [name]
      : name === 'Other'
        ? spendAllCats.filter((c) => !COLORED_GROUPS.includes(groupOf(c) as never))
        : spendAllCats.filter((c) => groupOf(c) === name)
  const scopeBand = (name: string, mode: 'only' | 'exclude') => {
    const cats = bandCats(name)
    const selected = measure === 'income' ? selectedIncome : selectedSpend
    const setSel = measure === 'income' ? setIncomeSel : setSpendSel
    setSel(mode === 'only' ? new Set(cats) : new Set([...selected].filter((c) => !cats.includes(c))))
  }

  // ---- Over time ----------------------------------------------------------
  // Spending stacks by GROUP, summing only the SELECTED categories within each
  // group — so deselecting one category shrinks its band, deselecting a whole
  // group drops it. Series keyed 'g:<group>'.
  const timeRows = useMemo(() => {
    const map = new Map<string, { key: string; income: number; spending: number; series: Record<string, number> }>()
    for (const m of sourceRows) {
      const key = bucketOf(m.month, bucket)
      let row = map.get(key)
      if (!row) {
        row = { key, income: 0, spending: 0, series: {} }
        map.set(key, row)
      }
      row.income += m.income
      row.spending += m.spending
      for (const [cat, v] of Object.entries(m.byCategory)) {
        if (!selectedSpend.has(cat)) continue
        const slot = COLORED_GROUPS.includes(groupOf(cat) as never) ? groupOf(cat) : 'Other'
        row.series['g:' + slot] = (row.series['g:' + slot] ?? 0) + v
      }
    }
    return [...map.values()]
      .sort((a, b) => (a.key < b.key ? -1 : 1))
      .map((r) => ({
        key: r.key,
        income: Math.round(r.income),
        spending: Math.round(r.spending),
        ...Object.fromEntries(Object.entries(r.series).map(([g, v]) => [g, Math.round(v)])),
      }))
  }, [sourceRows, bucket, selectedSpend])

  // The group bands actually present under the current selection (stack order).
  const shownTimeSeries = useMemo(() => {
    const present = new Set<string>()
    for (const r of timeRows as unknown as Record<string, unknown>[])
      for (const k of Object.keys(r)) if (k.startsWith('g:')) present.add(k.slice(2))
    return stackGroups.filter((g) => present.has(g)).map((g) => ({ key: 'g:' + g, name: g, color: groupColor(g) }))
  }, [timeRows, stackGroups])

  // Deep-link into the ledger with the current window + selected categories
  // (whichever of the selected/deselected sets is smaller keeps the URL short).
  // The ledger's category filter is HIERARCHICAL — a category living on both
  // rollup sides ("Income Tax": payments AND refunds) is two distinct leaves,
  // "income|Name" / "spend|Name" — so every category token a drill emits is
  // qualified with the measure's side, and a whole-side link names the side's
  // group ("Income" / "Expenses", which expand one-sided over there). A bare
  // name would match both sides and mix refunds into a spending drill.
  const sideTok = (c: string) => `${measure === 'income' ? 'income' : 'spend'}|${c}`
  const sideBase = measure === 'income' ? '&groups=Income' : '&groups=Expenses'
  const txnLinkForScope = (extra: string) =>
    `/finance/transactions?from=${startMonth}&to=${endMonth}${extra}`
  const scopeCatsLink = (selected: string[], all: string[]) => {
    if (selected.length >= all.length) return sideBase
    const unchecked = all.filter((c) => !selected.includes(c))
    return selected.length <= unchecked.length
      ? `&cats=${encodeURIComponent(selected.map(sideTok).join(','))}`
      : `${sideBase}&excat=${encodeURIComponent(unchecked.map(sideTok).join(','))}`
  }
  const timeScopeLink = txnLinkForScope(
    measure === 'income'
      ? scopeCatsLink([...selectedIncome], incomeCatsList)
      : scopeCatsLink([...selectedSpend], spendAllCats),
  )

  // Range total for what's plotted (selected series only). Summed from the
  // UNROUNDED monthly rows and rounded once — timeRows rounds per bucket for
  // display, and summing ~52 rounded weekly buckets drifts dollars off the
  // true total the Transactions page shows.
  const timeTotal = useMemo(() => {
    if (measure === 'income') {
      let s = 0
      for (const t of inRange) if (isIncome(t, tagPolicy) && selectedIncome.has(t.category)) s += t.amount
      return Math.round(s)
    }
    let sum = 0
    for (const m of sourceRows)
      for (const [cat, v] of Object.entries(m.byCategory)) if (selectedSpend.has(cat)) sum += v
    return Math.round(sum)
  }, [measure, sourceRows, selectedSpend, inRange, tagPolicy, selectedIncome])

  // Per-bucket average of what's plotted: the exact range total (unrounded
  // source, same selection scope as the bars) spread over the rendered
  // buckets. Partial edge buckets count — the line answers "what did a
  // typical <bucket> in this window look like", clipped edges included.
  const timeAvg = timeRows.length ? Math.round(timeTotal / timeRows.length) : 0

  // ---- Income by category over time ---------------------------------------
  // Rollups don't itemize income, so this scans the in-range transactions,
  // limited to the SELECTED income categories. Series keys use an 'i:' prefix.
  const incomeTime = useMemo(() => {
    if (measure !== 'income') return { rows: timeRows as Record<string, unknown>[], cats: [] as string[] }
    const byKey = new Map(timeRows.map((r) => [r.key, { ...r } as Record<string, unknown>]))
    const acc = new Map<string, Map<string, number>>()
    const totals = new Map<string, number>()
    for (const t of inRange) {
      if (!isIncome(t, tagPolicy) || !selectedIncome.has(t.category)) continue
      const key = bucketOf(t.date, bucket)
      if (!byKey.has(key)) continue
      let cats = acc.get(key)
      if (!cats) acc.set(key, (cats = new Map()))
      cats.set(t.category, (cats.get(t.category) ?? 0) + t.amount)
      totals.set(t.category, (totals.get(t.category) ?? 0) + t.amount)
    }
    for (const [key, cats] of acc) {
      const row = byKey.get(key)!
      for (const [c, v] of cats) row['i:' + c] = Math.round(v)
    }
    return {
      rows: [...byKey.values()],
      cats: [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c),
    }
  }, [measure, timeRows, inRange, bucket, tagPolicy, selectedIncome])

  // A drilled bucket key from the URL is only honored when it matches a bar
  // actually plotted under the current range + granularity — a stale key
  // (changed range, different auto-bucket) silently closes the drill instead
  // of rendering an "undefined, NaN" card.
  const drillBucket = useMemo(() => {
    if (!drillBucketParam) return null
    const keys = measure === 'income' ? incomeTime.rows.map((r) => r.key) : timeRows.map((r) => r.key)
    return keys.includes(drillBucketParam) ? drillBucketParam : null
  }, [drillBucketParam, measure, incomeTime, timeRows])

  // `?g=` is shared by both drills: in the by-category view it names the
  // drilled group outright, and in the over-time view it names a group drilled
  // WITHIN a bucket. The second only means anything when a bucket is actually
  // open, and only for spending — income has no groups, so its slices are
  // already leaf categories. A `g` that fails either test is ignored rather
  // than rendering an empty second level.
  const bucketGroup = view === 'time' && drillBucket && measure === 'spending' ? drillGroup : null

  // ---- Bucket drill-down (category pie for one clicked bucket) -------------
  // Spending breaks down by GROUP; income has no groups, so it breaks down by
  // income category directly (scanned from the raw transactions).
  const drillBucketGroups = useMemo(() => {
    if (!drillBucket) return null
    const totals: Record<string, number> = {}
    if (measure === 'income') {
      const [from, to] = bucketRange(drillBucket, bucket)
      for (const t of inRange) {
        if (t.date < from || t.date > to || !isIncome(t, tagPolicy) || !selectedIncome.has(t.category)) continue
        totals[t.category] = (totals[t.category] ?? 0) + t.amount
      }
      return totals
    }
    for (const m of sourceRows) {
      if (bucketOf(m.month, bucket) !== drillBucket) continue
      for (const [cat, v] of Object.entries(m.byCategory)) {
        if (!selectedSpend.has(cat)) continue // scope the pie like the bar it came from
        totals[groupOf(cat)] = (totals[groupOf(cat)] ?? 0) + v
      }
    }
    return totals
  }, [sourceRows, bucket, drillBucket, measure, inRange, tagPolicy, selectedIncome, selectedSpend])

  // Second level of the bucket drill: the clicked group's tier-2 categories
  // within the SAME bucket. Mirrors `drillCategories` (the by-category view's
  // drill), narrowed from the whole range down to one bar.
  const drillBucketCategories = useMemo(() => {
    if (!drillBucket || !bucketGroup) return []
    const totals = new Map<string, number>()
    for (const m of sourceRows) {
      if (bucketOf(m.month, bucket) !== drillBucket) continue
      for (const [cat, v] of Object.entries(m.byCategory)) {
        if (!selectedSpend.has(cat)) continue // stay within the active scope
        const g = groupOf(cat)
        const inDrill = bucketGroup === 'Other' ? !COLORED_GROUPS.includes(g as never) : g === bucketGroup
        if (!inDrill) continue
        totals.set(cat, (totals.get(cat) ?? 0) + v)
      }
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1])
  }, [sourceRows, bucket, drillBucket, bucketGroup, selectedSpend])

  // Groups you can hop between while drilled into a bucket — the bucket's OWN
  // groups, not the range's, so a pill never opens an empty sub-category donut.
  const bucketDrillTargets = useMemo(() => {
    if (!drillBucketGroups) return []
    const entries = Object.entries(drillBucketGroups).filter(([, v]) => v > 0.005)
    const colored = entries
      .filter(([g]) => COLORED_GROUPS.includes(g as never))
      .sort((a, b) => b[1] - a[1])
      .map(([g]) => g)
    const hasOther = entries.some(([g]) => !COLORED_GROUPS.includes(g as never))
    return hasOther ? [...colored, 'Other'] : colored
  }, [drillBucketGroups])
  const bucketDrillBase = bucketGroup ? groupColor(bucketGroup) : OTHER_COLOR

  // Ledger link confined to the drilled bucket's dates rather than the whole
  // plotted range.
  const bucketTxnLink = (extra: string) => {
    const [from, to] = drillBucket ? bucketRange(drillBucket, bucket) : [startMonth, endMonth]
    return `/finance/transactions?from=${from}&to=${to}${extra}`
  }

  // A GROUP-level ledger link emits the group's SELECTED categories rather than
  // `&groups=<g>`. A bare group link ignores the category scope, so a bar drawn
  // with Rent filtered out would open a ledger that silently puts Rent back —
  // the totals wouldn't match the band that was clicked.
  const groupScopeExtra = (g: string) => {
    const cats = bandCats(g).filter((c) => selectedSpend.has(c))
    return cats.length
      ? `&cats=${encodeURIComponent(cats.map(sideTok).join(','))}`
      : `&groups=${encodeURIComponent(g)}`
  }

  // ---- By category --------------------------------------------------------
  // Spending by group/category sums the precomputed statements; the income
  // measure ranks income categories from raw transactions (rollups only
  // itemize spending).
  const byGroup = useMemo(() => {
    const totals: Record<string, number> = {}
    if (view !== 'category') return totals // only the category view reads this
    if (measure === 'income') {
      for (const t of inRange) {
        if (!isIncome(t, tagPolicy) || !selectedIncome.has(t.category)) continue
        // Signed sum — a reversal in an income category nets against it.
        totals[t.category] = (totals[t.category] ?? 0) + t.amount
      }
    } else {
      // Sum the SELECTED categories (from byCategory), re-slotted to their group,
      // so the category scope shrinks each group band exactly like over-time.
      for (const m of monthsInRange) {
        for (const [cat, v] of Object.entries(m.byCategory)) {
          if (!selectedSpend.has(cat)) continue
          const g = groupOf(cat)
          totals[g] = (totals[g] ?? 0) + v
        }
      }
    }
    return totals
  }, [view, inRange, monthsInRange, measure, tagPolicy, selectedIncome, selectedSpend])

  const drillCategories = useMemo(() => {
    if (!drillGroup) return []
    const totals = new Map<string, number>()
    for (const m of monthsInRange) {
      for (const [category, v] of Object.entries(m.byCategory)) {
        if (!selectedSpend.has(category)) continue // stay within the active scope
        const g = groupOf(category)
        const inDrill = drillGroup === 'Other' ? !COLORED_GROUPS.includes(g as never) : g === drillGroup
        if (!inDrill) continue
        totals.set(category, (totals.get(category) ?? 0) + v)
      }
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1])
  }, [monthsInRange, drillGroup, selectedSpend])

  // Groups you can hop between while drilled in (palette groups present, then
  // the folded "Other"), largest first.
  const drillTargets = useMemo(() => {
    const entries = Object.entries(byGroup).filter(([, v]) => v > 0.005)
    const colored = entries
      .filter(([g]) => COLORED_GROUPS.includes(g as never))
      .sort((a, b) => b[1] - a[1])
      .map(([g]) => g)
    const hasOther = entries.some(([g]) => !COLORED_GROUPS.includes(g as never))
    return hasOther ? [...colored, 'Other'] : colored
  }, [byGroup])
  const drillBase = drillGroup ? groupColor(drillGroup) : OTHER_COLOR

  // ---- By merchant --------------------------------------------------------
  const merchants = useMemo(() => {
    const totals = new Map<string, { total: number; count: number }>()
    if (view !== 'merchant') return [] as [string, { total: number; count: number }][] // full-ledger scan; skip unless shown
    for (const t of inRange) {
      if (measure === 'income' ? !isIncome(t, tagPolicy) : !isSpending(t, tagPolicy)) continue
      if (!catInScope(t.category)) continue // carry the category scope from the other tabs
      const key = t.description
      const cur = totals.get(key) ?? { total: 0, count: 0 }
      // Signed nets: a merchant's returns reduce its spending total rather
      // than inflating it (and an income clawback reduces that source).
      cur.total += measure === 'income' ? t.amount : -t.amount
      cur.count += 1
      totals.set(key, cur)
    }
    return [...totals.entries()].sort((a, b) => b[1].total - a[1].total)
  }, [view, inRange, measure, tagPolicy, selectedIncome, selectedSpend])

  const rangeLabel = `${fmtMonthShort(startMonth)} – ${fmtMonthShort(endMonth)}`

  return (
    <div className="space-y-4">
      <PageHeader title="Trends">{control}</PageHeader>

      {/* Filter row — scopes everything below */}
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Measure"
          options={[
            { value: 'spending', label: 'Spending' },
            { value: 'income', label: 'Income' },
          ]}
          value={measure}
          onChange={setMeasure}
        />
        <Segmented
          ariaLabel="Breakdown"
          options={[
            { value: 'time', label: 'Over time' },
            { value: 'category' as View, label: 'By category' },
            { value: 'merchant' as View, label: 'By merchant' },
          ]}
          value={view}
          onChange={setView}
        />
        {/* Category scope — a global filter: set it on any tab and it carries to
            the others (over-time bands, by-category donut, by-merchant list). */}
        {measure === 'spending' ? (
          <CategoryMenu tree={spendTree} allCats={spendAllCats} selected={selectedSpend} onChange={setSpendSel} label="All spending" />
        ) : (
          <CategoryMenu tree={incomeTree} allCats={incomeCatsList} selected={selectedIncome} onChange={setIncomeSel} label="All income" />
        )}
        <TagMenu />
        {(exclSpend.length > 0 || exclIncome.length > 0 || !tagPolicyIsDefault(tagPolicy)) && (
          <ResetFiltersChip
            onReset={() => {
              setExclSpend([])
              setExclIncome([])
              resetTagPolicy()
            }}
          />
        )}
      </div>

      {view === 'time' && (
        <>
        <Card
          title={
            measure === 'spending'
              ? `Spending over time · by ${bucket}`
              : `Income over time · by ${bucket}`
          }
          subtitle="Click a bar to break the period down by category"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                ariaLabel="Density"
                options={BUCKET_OPTIONS}
                value={bucketPref}
                onChange={(b) => {
                  setBucketPref(b)
                  // A drilled bucket key is granularity-specific; drop it so
                  // we never render a stale drilldown after re-bucketing.
                  // Density isn't a navigation step — replace, don't push.
                  const q = new URLSearchParams(window.location.search)
                  q.delete('b')
                  const qs = q.toString()
                  window.history.replaceState(null, '', qs ? `/finance/trends?${qs}` : '/finance/trends')
                }}
              />
              <button
                onClick={() => setShowAvg(showAvg === '1' ? '0' : '1')}
                aria-pressed={showAvg === '1'}
                title={`Toggle the per-${bucket} average line`}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  showAvg === '1'
                    ? 'border-transparent bg-accent/15 text-accent-ink'
                    : 'border-edge text-ink-3 hover:text-ink-2'
                }`}
              >
                Avg
              </button>
              <a href={timeScopeLink} className="text-xs font-medium text-accent-ink hover:underline">
                View all transactions →
              </a>
            </div>
          }
        >
          {/* Range total for what's plotted */}
          <div className="mb-2 flex flex-wrap items-baseline gap-2">
            <span className="text-2xl font-semibold tracking-tight tabular-nums">{fmtUsd(timeTotal)}</span>
            <span className="text-xs text-ink-3">
              total {measure} · {rangeLabel}
              {showAvg === '1' && timeRows.length > 0 && ` · avg ${fmtUsd(timeAvg)}/${bucket}`}
            </span>
          </div>
          <div className="h-80" ref={chartRef}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={measure === 'income' ? incomeTime.rows : timeRows}
                margin={CHART_MARGIN}
                onClick={(s) => {
                  const raw = s?.activeLabel
                  if (raw == null || raw === '') return
                  const k = String(raw)
                  setDrillBucket(drillBucket === k ? null : k)
                }}
                className="[&_.recharts-bar-rectangle]:cursor-pointer"
              >
                <CartesianGrid {...gridProps} />
                <XAxis
                  dataKey="key"
                  {...xAxisProps}
                  tickFormatter={(k: string) => fmtBucketShort(k, bucket)}
                  minTickGap={32}
                />
                <YAxis {...yAxisProps} />
                <Tooltip
                  cursor={barCursor}
                  content={<ChartTooltip labelFormatter={(k) => fmtBucket(k, bucket)} showTotal />}
                />
                {measure === 'spending' &&
                  shownTimeSeries.map((s, i) => (
                    <Bar             key={s.key}
                      dataKey={s.key}
                      name={s.name}
                      stackId="s"
                      fill={s.color}
                      stroke="var(--surface-1)"
                      strokeWidth={1}
                      maxBarSize={BAR_MAX}
                      radius={i === shownTimeSeries.length - 1 ? BAR_RADIUS : undefined}
                    />
                  ))}
                {measure === 'income' &&
                  incomeTime.cats.map((c, i) => (
                    <Bar
                      key={c}
                      dataKey={'i:' + c}
                      name={c}
                      stackId="s"
                      fill={shadeOf('var(--series-2)', i)}
                      stroke="var(--surface-1)"
                      strokeWidth={1}
                      maxBarSize={BAR_MAX}
                      radius={i === incomeTime.cats.length - 1 ? BAR_RADIUS : undefined}
                    />
                  ))}
                {showAvg === '1' && timeRows.length > 0 && (
                  <ReferenceLine
                    y={timeAvg}
                    stroke="var(--text-muted)"
                    strokeDasharray="5 4"
                    strokeWidth={1.5}
                    ifOverflow="extendDomain"
                    label={{
                      value: `avg ${fmtUsd(timeAvg)}/${bucket}`,
                      position: 'insideTopRight',
                      dy: -7,
                      fill: 'var(--text-muted)',
                      fontSize: 11,
                    }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {/* Color key for the stacked bands, in stack order (bottom band
              first) so the legend reads the same way the bars do. */}
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            {(measure === 'spending'
              ? shownTimeSeries.map((s) => ({ key: s.key, label: s.name, color: s.color }))
              : incomeTime.cats.map((c, i) => ({ key: c, label: c, color: shadeOf('var(--series-2)', i) }))
            ).map((s) => (
              <ScopeMenu
                key={s.key}
                onOnly={() => scopeBand(s.label, 'only')}
                onExclude={() => scopeBand(s.label, 'exclude')}
              >
                <LegendKey color={s.color} label={s.label} />
              </ScopeMenu>
            ))}
          </div>
        </Card>

        {/* Bucket drill, two levels deep — the same shape as the by-category
            view, but everything is confined to the clicked bar's dates:
              bar → groups in that bucket → that group's sub-categories → ledger
            Income stops at one level: it has no groups, so its slices are
            already leaf categories and go straight to the ledger. */}
        {drillBucket && drillBucketGroups && (bucketGroup ? (
          <Card
            title={`${bucketGroup} · ${fmtBucket(drillBucket, bucket)}`}
            subtitle="Sub-categories · click a slice to see transactions"
            actions={
              <span className="flex items-center gap-3">
                <a
                  href={bucketTxnLink(groupScopeExtra(bucketGroup))}
                  className="text-xs font-medium text-accent-ink hover:underline"
                >
                  View all transactions →
                </a>
                <button
                  onClick={() => setDrillGroup(null)}
                  className="text-xs font-medium text-accent-ink hover:underline"
                >
                  ← All categories
                </button>
                <button
                  onClick={() => setDrillBucket(null)}
                  className="text-xs font-medium text-accent-ink hover:underline"
                >
                  Close
                </button>
              </span>
            }
          >
            {/* Hop pills — swap groups without climbing back out of the bucket. */}
            <div className="mb-4 flex flex-wrap gap-1.5">
              {bucketDrillTargets.map((g) => (
                <Pill
                  key={g}
                  label={g}
                  color={groupColor(g)}
                  active={g === bucketGroup}
                  onClick={() => setDrillGroup(g)}
                />
              ))}
            </div>
            {drillBucketCategories.length ? (
              <SpendingDonut
                byGroup={Object.fromEntries(drillBucketCategories)}
                fold={false}
                colorFor={(_, i) => shadeOf(bucketDrillBase, i)}
                onSelect={(cat) => goLedger(bucketTxnLink(`&cats=${encodeURIComponent(sideTok(cat))}`))}
              />
            ) : (
              <p className="text-sm text-ink-3">Nothing in this period.</p>
            )}
          </Card>
        ) : (
          <Card
            title={`${fmtBucket(drillBucket, bucket)} ${measure}`}
            subtitle={
              measure === 'spending'
                ? 'Click a slice to break it into sub-categories'
                : 'Click a slice to open its transactions'
            }
            actions={
              <button
                onClick={() => setDrillBucket(null)}
                className="text-xs font-medium text-accent-ink hover:underline"
              >
                Close
              </button>
            }
          >
            {/* Spending slices are groups (fixed palette, folded Other);
                income slices are the categories themselves, shaded green. */}
            <SpendingDonut
              byGroup={drillBucketGroups}
              fold={measure === 'spending'}
              colorFor={measure === 'income' ? (_, i) => shadeOf('var(--series-2)', i) : undefined}
              label={measure === 'income' ? 'Income' : 'Spending'}
              onSelect={(g) => {
                if (measure === 'spending') setDrillGroup(g)
                else goLedger(bucketTxnLink(`&cats=${encodeURIComponent(sideTok(g))}`))
              }}
            />
          </Card>
        ))}
        </>
      )}

      {view === 'category' && measure === 'spending' && (
        !drillGroup ? (
          <Card
            title={`Spending by category · ${rangeLabel}`}
            subtitle="Click a slice to drill into a category"
            actions={
              <a href={txnLinkForScope(sideBase)} className="text-xs font-medium text-accent-ink hover:underline">
                View all transactions →
              </a>
            }
          >
            <SpendingDonut byGroup={byGroup} onSelect={(g) => setDrillGroup(g)} onScope={scopeBand} />
          </Card>
        ) : (
          <Card
            title={`${drillGroup} · ${rangeLabel}`}
            subtitle="Sub-categories · click a slice to see transactions"
            actions={
              <span className="flex items-center gap-3">
                <a
                  href={txnLinkForScope(`&groups=${encodeURIComponent(drillGroup)}`)}
                  className="text-xs font-medium text-accent-ink hover:underline"
                >
                  View all transactions →
                </a>
                <button
                  onClick={() => setDrillGroup(null)}
                  className="text-xs font-medium text-accent-ink hover:underline"
                >
                  ← All categories
                </button>
              </span>
            }
          >
            {/* Hop pills — jump straight to another category */}
            <div className="mb-4 flex flex-wrap gap-1.5">
              {drillTargets.map((g) => (
                <Pill
                  key={g}
                  label={g}
                  color={groupColor(g)}
                  active={g === drillGroup}
                  onClick={() => setDrillGroup(g)}
                />
              ))}
            </div>
            {drillCategories.length ? (
              <SpendingDonut
                byGroup={Object.fromEntries(drillCategories)}
                fold={false}
                colorFor={(_, i) => shadeOf(drillBase, i)}
                onSelect={(cat) => goLedger(txnLinkForScope(`&cats=${encodeURIComponent(sideTok(cat))}`))}
              />
            ) : (
              <p className="text-sm text-ink-3">Nothing in this period.</p>
            )}
          </Card>
        )
      )}

      {view === 'category' && measure === 'income' && (
        <Card
          title={`Income by category · ${rangeLabel}`}
          subtitle="Click a category to open its transactions"
          actions={
            <a
              href={txnLinkForScope('&groups=Income')}
              className="text-xs font-medium text-accent-ink hover:underline"
            >
              View all transactions →
            </a>
          }
        >
          {txnsLoading ? (
            <p className="py-4 text-center text-sm text-ink-3">Loading transactions…</p>
          ) : (
            // Income categories have no sub-categories — a slice goes
            // straight to its ledger view, pre-filtered.
            <SpendingDonut
              byGroup={byGroup}
              fold={false}
              colorFor={(_, i) => shadeOf('var(--series-2)', i)}
              label="Income"
              onSelect={(cat) => goLedger(txnLinkForScope(`&cats=${encodeURIComponent(sideTok(cat))}`))}
              onScope={scopeBand}
            />
          )}
        </Card>
      )}

      {view === 'merchant' && (
        <Card
          title={`${measure === 'income' ? 'Income' : 'Spending'} by merchant · ${
            merchantLimitN < merchants.length ? `top ${merchantLimitN} of ` : ''
          }${merchants.length} · ${rangeLabel}`}
        >
          {txnsLoading && (
            <p className="py-4 text-center text-sm text-ink-3">Loading transactions…</p>
          )}
          {!txnsLoading && (
          <>
          {merchants.length > MERCHANT_LIMITS[0].n && (
            <div className="mb-3 flex items-center gap-2 text-xs text-ink-3">
              <span>Show</span>
              <Segmented
                ariaLabel="Merchants shown"
                options={MERCHANT_LIMITS}
                value={merchantLimit}
                onChange={setMerchantLimit}
              />
            </div>
          )}
          <RankedBars
            rows={merchants.slice(0, merchantLimitN).map(([name, v]) => [name, v.total] as [string, number])}
            counts={new Map(merchants.map(([name, v]) => [name, v.count]))}
            color="var(--series-1)"
            // Carry the range + active category scope into the ledger, then
            // narrow to this merchant — so a click lands on the same rows the
            // bar counted (merchant ∩ the category filter set on any tab).
            linkTo={(m) =>
              `${txnLinkForScope(
                measure === 'income'
                  ? scopeCatsLink([...selectedIncome], incomeCatsList)
                  : scopeCatsLink([...selectedSpend], spendAllCats),
              )}&q=${encodeURIComponent(m)}`
            }
          />
          </>
          )}
        </Card>
      )}
    </div>
  )
}

/** Ranked horizontal magnitude bars — one series, one hue, values labeled. */
function RankedBars({
  rows,
  color,
  counts,
  linkTo,
}: {
  rows: [string, number][]
  color: string
  counts?: Map<string, number>
  linkTo?: (name: string) => string
}) {
  if (!rows.length) return <p className="text-sm text-ink-3">Nothing in this period.</p>
  const max = rows[0][1]
  return (
    <ul className="space-y-1">
      {rows.map(([name, value]) => {
        const inner = (
          <>
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate font-medium">{name}</span>
              <span className="shrink-0 tabular-nums text-ink">
                {fmtUsd(value)}
                {counts && <span className="ml-1.5 text-xs text-ink-3">×{counts.get(name)}</span>}
              </span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-3">
              <div
                className="h-full rounded-full"
                style={{ width: `${Math.max(1, (value / max) * 100)}%`, background: color }}
              />
            </div>
          </>
        )
        return (
          <li key={name}>
            {linkTo ? (
              <a href={linkTo(name)} className="block rounded-lg px-2 py-1.5 hover:bg-surface-3/60">
                {inner}
              </a>
            ) : (
              <div className="px-2 py-1.5">{inner}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
